import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeRuExtra from '@angular/common/locales/extra/ru';
import localeRu from '@angular/common/locales/ru';
import { ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckboxDefaultOptions } from '@angular/material/checkbox';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorDefaultOptions,
  MatPaginatorIntl,
} from '@angular/material/paginator';
import {
  MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,
  MatProgressSpinnerDefaultOptions,
} from '@angular/material/progress-spinner';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { ru as ruDateLocale } from 'date-fns/locale';
import { provideEchartsCore } from 'ngx-echarts';
import { Subject } from 'rxjs';

import { environment } from '@app/environment';

import packageJson from '../../package.json';
import {
  apiBaseUrlInterceptor,
  apiDeviceInfoInterceptor,
  apiExceptionInterceptor,
  apiRefreshTokenInterceptor,
  apiTokenInterceptor,
} from './api/interceptors';
import { routes } from './app.routes';
import { provideAuth } from './core/auth/auth.provider';
import { AuthState } from './core/auth/store/auth.state';
import { provideSvgIcons } from './core/svg-icon/svg-icon.module';
import { provideTabManagement } from './core/tab-management';
import { APP_VERSION } from './core/tokens';
import { provideUpdater } from './core/updater/updater.factory';
import { provideAlarmSosDialog } from './features/alarm/alarm-sos-dialog';
import { DeviceState } from './store/device/device.state';
import { ImGroupState } from './store/im/im-group.state';

registerLocaleData(localeRu, localeRuExtra);

const MAT_FORM_FIELD_OPTIONS: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  subscriptSizing: 'dynamic',
};

const MAT_PAGINATOR_OPTIONS: MatPaginatorDefaultOptions = {
  formFieldAppearance: 'outline',
  hidePageSize: true,
};

const MAT_CHECKBOX_OPTIONS: MatCheckboxDefaultOptions = {
  color: 'primary',
};

const MAP_PROGRESS_SPINNER_OPTIONS: MatProgressSpinnerDefaultOptions = {
  color: 'primary',
};

class PaginatorIntl implements MatPaginatorIntl {
  changes = new Subject<void>();

  firstPageLabel = `Первая страница`;
  itemsPerPageLabel = `На странице:`;
  lastPageLabel = `Последняя страница`;

  nextPageLabel = 'Следующая';
  previousPageLabel = 'Предыдущая';

  getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0) {
      return `Страница 1 из 1`;
    }
    const amountPages = Math.ceil(length / pageSize);
    return `Страница ${page + 1} из ${amountPages}`;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: MAT_FORM_FIELD_OPTIONS,
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: MAT_PAGINATOR_OPTIONS,
    },
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: MAT_CHECKBOX_OPTIONS,
    },
    {
      provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,
      useValue: MAP_PROGRESS_SPINNER_OPTIONS,
    },
    {
      provide: LOCALE_ID,
      useValue: 'ru',
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: ruDateLocale,
    },
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
    },
    {
      provide: APP_VERSION,
      useValue: packageJson.version,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !environment.isLocal,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(
      withInterceptors([
        apiBaseUrlInterceptor,
        apiExceptionInterceptor,
        apiTokenInterceptor,
        apiRefreshTokenInterceptor,
        apiDeviceInfoInterceptor,
      ]),
    ),
    provideStore(
      [AuthState, DeviceState, ImGroupState],
      withNgxsStoragePlugin({
        keys: ['auth.accessToken', 'auth.accessTokenExpiresAt', 'auth.refreshToken', 'auth.refreshTokenExpiresAt'],
      }),
    ),
    provideDateFnsAdapter(),
    provideEchartsCore({
      echarts: () => import('echarts'),
    }),
    provideUpdater(),
    provideAuth(),
    provideSvgIcons(),
    provideAlarmSosDialog(),
    provideTabManagement(),
  ],
};
